import { useEffect, useRef, useState } from 'react';
import { AVOIDED_TABLE_TYPE, GENERATED_TABLE_TYPE, PDF_TYPE } from './HelperConstants';
import { TRANSITION_LENGTH_INT } from '../../styles/emit-styles/CardStyles';
import { DateTime } from "luxon";
import convert from 'convert';

export function roundValue(value) {
    if (value === 0) {
        return '0';
    } else if (value < 0.1) {
        return value < 0.01 ? '<0.01' : (Math.round(value * 100) / 100).toFixed(2).toLocaleString();
    } else if (value < 9.99) {
        return (Math.round(value * 10) / 10).toFixed(1).toLocaleString();
    } else {
        return Math.round(value).toLocaleString();
    }
}

const bigEmissionsTypes = ['ghg', 'co2', 'bioco2'];
const mediumEmissionsTypes = ['co', 'ch4', 'nox'];
const smallEmissionsTypes = ['pm10', 'pm25'];
const emissionsTypes = [...bigEmissionsTypes, ...mediumEmissionsTypes, ...smallEmissionsTypes];
const currencyTypes = ['fuelCostSaved'];

export function convertData(type, value, metricUnitPrefs, macroScale, userSettings) {
    if (value === null || value === undefined) return value; // Early return so that null values don't get run through the conversion (the convert library will update nulls to 0s)
    if (emissionsTypes.some((t) => type.toLowerCase().includes(t)) && type !== "fuelCostSaved") { // we need the fuelCostSaved check because "cost" includes "co" - there is probably a better way to run these checks, but I couldn't think of one that doesn't involve checking for every single possible key from the compilation functions
        const units = provideEmissionsUnitSystem(metricUnitPrefs.emissions);
        if (bigEmissionsTypes.some((t) => type.toLowerCase().includes(t))) {
            return convert(value, 'grams').to(macroScale ? units.large.long : units.medium.long);
        } else if (mediumEmissionsTypes.some((t) => type.toLowerCase().includes(t))) {
            // Note that it's important for big emissions to be checked first (medium emissions includes 'co', but that string is also included in 'co2')
            return convert(value, 'grams').to(macroScale ? units.medium.long : units.small.long);
        } else if (smallEmissionsTypes.some((t) => type.toLowerCase().includes(t))) {
            return convert(value, 'grams').to(units.small.long);
            // note no scale conversion for small emissions types -  they will always be in the smallest unit available. Conversion needed here for if viewing imperial (convert g to oz)
        }
    } else if (type.toLowerCase().includes("distance")) {
        return metricUnitPrefs.distance ? value : convert(value, 'kilometers').to('miles');
    } else if (type.toLowerCase().includes("fuelunits")) {
        return metricUnitPrefs.volume ? value : convert(value, 'liters').to('gallons');
    } else if (currencyTypes.includes(type)) {
        return convertCurrency(userSettings, value);
    } else {
        // no conversion needed for other values, like simple counts (ie. 'cngVehicles')
        return value;
    }
}

export function formatData(type, value, userSettings, showValues = true, rawNumbers = false) {
    if (type === 'vin') return value ?? "Not Provided";
    if (typeof value === 'string' || type === 'year') return value; // Early return for first several table columns - no modification needed, and don't want to format the year with commas
    if (!showValues || value === null || value === undefined) return '-';
    if (currencyTypes.includes(type)) {
        // Early return for currency type, as we want to do some custom output handling for these values.
        return `${userSettings.currency_symbol}${Math.round(value).toLocaleString()}`;
    } 
    return rawNumbers ? value : roundValue(value);
}

// TODO: I think all the measurement-related functions should ultimately live in the dashboard core repo, in a shared helper function library that is accesssible by every product repo. -NK

function convertCurrency(userSettings, value) {
    let conversion_rate = 1;

    // if user currency !== db currency, convert using applicable conversion rate
    if (userSettings.db_currency === 'EUR' && userSettings.currency === 'USD') {
        conversion_rate = userSettings.to_usd;
    }
    if (userSettings.db_currency === 'USD' && userSettings.currency === 'EUR') {
        conversion_rate = userSettings.to_eur;
    }
    return value * conversion_rate;
}

export function getEmissionsUnits(type, metric, macroScale, shorthand, value = null) {
    const unitSystem = provideEmissionsUnitSystem(metric);
    const len = shorthand ? 'short' : 'long';
    let singular = (value === 1);
    let units = null;

    if (bigEmissionsTypes.includes(type)) {
        units = macroScale ? unitSystem.large[len] : unitSystem.medium[len];
    } else if (mediumEmissionsTypes.includes(type)) {
        units = macroScale ? unitSystem.medium[len] : unitSystem.small[len];
    } else if (smallEmissionsTypes.includes(type)) {
        units = unitSystem.small[len];
    } else {
        // Should never happen, but including for future development/testing purposes -NK
        throw new Error('Invalid value type provided!');
    }

    return (singular || shorthand) ? units : `${units}s`;
}

export const provideEmissionsUnitSystem = (metric) => {
    if (metric) {
        return (
            {
                large: { long: 'metric ton', short: 'mt' },
                medium: { long: 'kilogram', short: 'kg' },
                small: { long: 'gram', short: 'g' }
            }
        )
    } else {
        return (
            {
                large: { long: 'short ton', short: 'st' },
                medium: { long: 'pound', short: 'lb' },
                small: { long: 'ounce', short: 'oz' }
            }
        )
    }
}

export function getDistanceUnits(metric, shorthand, value = null) {
    let singular = (value === 1);
    if (shorthand) {
        return metric ? 'km' : 'mi';
    } else {
        if (metric) {
            return singular ? 'kilometer' : 'kilometers';
        } else {
            return singular ? 'mile' : 'miles';
        }
    }
}

export function getFuelUnits(metric, shorthand, value = null) {
    let singular = (value === 1);
    if (shorthand) {
        return metric ? 'l' : 'gal';
    } else {
        if (metric) {
            return singular ? 'liter' : 'liters';
        } else {
            return singular ? 'gallon' : 'gallons';
        }
    }
}

export function formatMonthLabelShort(monthYear, settings) {
    if (typeof (monthYear) === 'string') {
        const [month, year] = monthYear.split("/");
        if (isNaN(parseInt(month)) || isNaN(parseInt(year)) || month < 1 || month > 12) return null;
        switch(settings.date_display){
        case 'dd/MM/yy':
            return `${month}/${year.slice(2,4)}`;
        case 'dd/MM/yyyy':
            return `${month}/${year}`;
        case 'MM/dd/yy':
            return `${month}/${year.slice(2,4)}`;
        case 'MM/dd/yyyy':
            return `${month}/${year}`;
        case 'yy/MM/dd':
            return `${year.slice(2,4)}/${month}`;
        case 'yyyy/MM/dd':
            return `${year}/${month}`;
        default:
            return monthYear;
    }
    } else return null;
}

export function formatMonthLabelLong(monthYear) {
    if (typeof (monthYear) === 'string') {
        const [month, year] = monthYear.split("/");
        const parsedMonth = parseInt(month);
        const parsedYear = parseInt(year);

        if (isNaN(parsedMonth) || isNaN(parsedYear) || parsedMonth < 1 || parsedMonth > 12) return null;

        const zeroBasedMonth = parsedMonth - 1;
        const date = new Date(parsedYear, zeroBasedMonth, 1); // Create a Date object
        const options = { year: 'numeric', month: 'long' };
        return date.toLocaleDateString('en-US', options); // Output example: "January 2024"
    } else return null;
}

export function formatWeekLabelShort(week) {
    if (week instanceof Date) {
        const options = { year: '2-digit', month: '2-digit', day: '2-digit' };
        return week.toLocaleDateString('en-US', options); // Output example: "01/07/24"
    } else return null;
}

export function formatWeekLabelLong(week) {
    if (week instanceof Date) {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return week.toLocaleDateString('en-US', options); // Output example: "January 7, 2024"
    } else return null;
}

export function filterDataTypes(data, dataTypes) {
    const filteredTypes = dataTypes.filter(d => data.find(e => e[d.id] !== 0 && e[d.id] !== undefined && e[d.id] !== null));
    let totalNumber = 0;

    if (data?.[0]) filteredTypes.forEach(t => totalNumber += data[0][t.id]);
    return [filteredTypes, totalNumber];
}

export function compileHomeViewGraphData(graphData, viewDataByMonth, splitByDrivetrain, showDiscreteData) {
    let homeViewGraphData = [];

    let cumulative = showDiscreteData ? '' : 'Cumulative';
    let dateBreakdown = viewDataByMonth ? 'monthly' : 'weekly';
    let dataKey = viewDataByMonth ? 'month' : 'week';

    graphData[`${dateBreakdown}Labels`]?.forEach((value, index) => {
        let newEntry = { [dataKey]: value }

         // aggregate data simply by generated vs. avoided, if not splitting by drivetrain (default)
        if (splitByDrivetrain) {
            
            newEntry['gasolineGenerated'] = graphData[`${dateBreakdown}GasolineGhgGenerated${cumulative}`][index];
            newEntry['dieselGenerated'] = graphData[`${dateBreakdown}DieselGhgGenerated${cumulative}`][index];
            newEntry['cngGenerated'] = graphData[`${dateBreakdown}CNGGhgGenerated${cumulative}`][index];
            newEntry['bevGenerated'] = graphData[`${dateBreakdown}BevGhgGenerated${cumulative}`][index];
            newEntry['phevGenerated'] = graphData[`${dateBreakdown}PhevGhgGenerated${cumulative}`][index];
            newEntry['bevAvoided'] = -1 * graphData[`${dateBreakdown}BevGhgAvoided${cumulative}`][index];
            newEntry['phevAvoided'] = -1 * graphData[`${dateBreakdown}PhevGhgAvoided${cumulative}`][index];
            newEntry['cngAvoided'] = -1 * graphData[`${dateBreakdown}CNGGhgAvoided${cumulative}`][index];
        } else {
            newEntry['generated'] = graphData[`${dateBreakdown}GhgGenerated${cumulative}`][index];
            newEntry['avoided'] = -1 * graphData[`${dateBreakdown}GhgAvoided${cumulative}`][index];
        }
        homeViewGraphData.push(newEntry);
    });

    return homeViewGraphData;
}

export function compileGeneratedEmissionsViewGraphData(graphData, viewDataByMonth, splitByDrivetrain, showDiscreteData) {
    let generatedViewGraphData = [];

    let cumulative = showDiscreteData ? '' : 'Cumulative';
    let dateBreakdown = viewDataByMonth ? 'monthly' : 'weekly';
    let dataKey = viewDataByMonth ? 'month' : 'week';

    graphData[`${dateBreakdown}Labels`]?.forEach((value, index) => {
        let newEntry = { [dataKey]: value }

         // aggregate data simply by generated vs. avoided, if not splitting by drivetrain (default)
        if (splitByDrivetrain) {
            
            newEntry['gasolineGenerated'] = graphData[`${dateBreakdown}GasolineGhgGenerated${cumulative}`][index];
            
            newEntry['dieselGenerated'] = graphData[`${dateBreakdown}DieselGhgGenerated${cumulative}`][index];
            newEntry['cngGenerated'] = graphData[`${dateBreakdown}CNGGhgGenerated${cumulative}`][index];
            newEntry['bevGenerated'] = graphData[`${dateBreakdown}BevGhgGenerated${cumulative}`][index];
            newEntry['phevGenerated'] = graphData[`${dateBreakdown}PhevGhgGenerated${cumulative}`][index];
        } else {
            newEntry['generated'] = graphData[`${dateBreakdown}GhgGenerated${cumulative}`][index];
        }
        generatedViewGraphData.push(newEntry);
    });

    return generatedViewGraphData;
}

export function compileAvoidedEmissionsViewGraphData(graphData, viewDataByMonth, splitByDrivetrain, showDiscreteData) {
    let avoidedViewGraphData = [];

    let cumulative = showDiscreteData ? '' : 'Cumulative';
    let dateBreakdown = viewDataByMonth ? 'monthly' : 'weekly';
    let dataKey = viewDataByMonth ? 'month' : 'week';

    graphData[`${dateBreakdown}Labels`]?.forEach((value, index) => {
        let newEntry = { [dataKey]: value }

         // aggregate data simply by generated vs. avoided, if not splitting by drivetrain (default)
        if (splitByDrivetrain) {
                    
            newEntry['bevAvoided'] = graphData[`${dateBreakdown}BevGhgAvoided${cumulative}`][index];
            newEntry['phevAvoided'] = graphData[`${dateBreakdown}PhevGhgAvoided${cumulative}`][index];
            newEntry['cngAvoided'] = graphData[`${dateBreakdown}CNGGhgAvoided${cumulative}`][index];
        } else {
            newEntry['avoided'] = graphData[`${dateBreakdown}GhgAvoided${cumulative}`][index];
        }
        avoidedViewGraphData.push(newEntry);
    });

    return avoidedViewGraphData;
}

export const compileFleetCompositionCardData = (summaryData) => [{
    breakdown: true,
    gasoline: summaryData.gasolineVehicles,
    diesel: summaryData.dieselVehicles,
    bev: summaryData.bevVehicles,
    phev: summaryData.phevVehicles,
    cng: summaryData.cngVehicles
}];

export const compileAltFuelCompositionCardData = (summaryData) => [{
    breakdown: true,
    bev: summaryData.bevVehicles,
    phev: summaryData.phevVehicles,
    cng: summaryData.cngVehicles
}];

export const compileFleetDistanceBreakdownData = (summaryData) => [{
    breakdown: true,
    trad: summaryData.tradFuelDistance,
    alt: summaryData.altFuelDistance
}];

export const compileDistanceBreakdownByDrivetrainData = (summaryData) => [{
    breakdown: true,
    gasoline: summaryData.gasolineDistance,
    diesel: summaryData.dieselDistance,
    bev: summaryData.bevDistance,
    phev: summaryData.phevDistance,
    cng: summaryData.cngDistance
}];

export const compileDistanceBreakdownByAltDrivetrainData = (summaryData) => [{
    breakdown: true,
    bev: summaryData.bevDistance,
    phev: summaryData.phevDistance,
    cng: summaryData.cngDistance
}];

export const compileGeneratedEmissionsByDrivetrainData = (summaryData) => [{
    breakdown: true,
    gasoline: summaryData.gasolineGhgGenerated,
    diesel: summaryData.dieselGhgGenerated,
    bev: summaryData.bevGhgGenerated,
    phev: summaryData.phevGhgGenerated,
    cng: summaryData.cngGhgGenerated
}];

export const compileAvoidedEmissionsByDrivetrainData = (summaryData) => [{
    breakdown: true,
    bev: summaryData.bevGhgAvoided,
    phev: summaryData.phevGhgAvoided,
    cng: summaryData.cngGhgAvoided
}];

function sanitizeDownloadString(str) {
    // Replace all forward slashes, underscores, and spaces with dashes
    const replaceWithDashes = str.replace(/[\s/_]/g, "-");
    // Remove non-alphanumeric characters, excluding dashes
    const sanitized = replaceWithDashes.replace(/[^a-zA-Z0-9-]/g, "");
    return sanitized;
}

export function buildDownloadFilename(dbDisplayName, type, userSettings) {
    let fileName = "sawatch-labs-emit-";
    if (type === AVOIDED_TABLE_TYPE) {
        fileName += "avoided-";
    }
    else if (type === GENERATED_TABLE_TYPE) {
        fileName += "generated-";
    }

    fileName += `emissions-report-${dbDisplayName}-`;
    fileName += `${formatTimestamp(userSettings, DateTime.local()).date}`
    fileName = sanitizeDownloadString(fileName);
    fileName += type === PDF_TYPE ? '.pdf' : '.xlsx';

    return fileName;
}

export function hasAltFuelVehicles(allVehicleData) {
    if (!allVehicleData || allVehicleData.length === 0) return null;
    else return allVehicleData.filter((v) => vehicleIsAltFuel(v)).length > 0;
}

export function vehicleIsAltFuel(vehicle) {
    return (vehicle.is_phev || vehicle.is_bev || vehicle.is_cng);
}

export function deriveMethodObjFromSettings(methodology) {
    if (methodology === 'ghgprotocol_2.0.0') {
        return { value: 'ghgprotocol_2.0.0', label: 'GHG Protocol 2.0.0' }
    } else if (methodology === 'moves3') {
        return { value: 'moves3', label: 'MOVES3' }
    } else {
        // Assume GHGP if no methodology provided via DB settings object
        return { value: 'ghgprotocol_2.0.0', label: 'GHG Protocol 2.0.0' }
    }
}

export function useHoverAnimation() {
    // The following nifty logic block allows for the fade in/fade out effects on the tooltip in recharts components.
    // Packaged up into a custom hook for further extensibility!

    // This was surprising difficult to create in recharts natively, due to the behavior of the tooltip being that
    // it is immediately destroyed when the user moves away from the target element it's being displayed for.
    const [tooltipActive, setTooltipActive] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const timeoutRef = useRef(null);

    const showTooltip = () => {
        clearTimeout(timeoutRef.current); // Clear any pending timeouts
        setTooltipActive(true);
        setTooltipVisible(true);
    };

    const hideTooltip = () => {
        setTooltipVisible(false); // Immediately start to fade the tooltip

        timeoutRef.current = setTimeout(() => {
            setTooltipActive(false); // After fade transition ends, set tooltipActive to false
        }, TRANSITION_LENGTH_INT);
    };

    useEffect(() => {
        return () => clearTimeout(timeoutRef.current); // Clear timeout on unmount
    }, []);

    return [tooltipActive, tooltipVisible, showTooltip, hideTooltip];
}

export const pageHasData = (page, summaryData) => {
    if (summaryData === null) return false;

    if (page === 'home' || page === 'generated-emissions') return (summaryData.ghgGenerated !== null && summaryData.ghgGenerated !== 0);
    if (page === 'avoided-emissions') return (summaryData.ghgAvoided !== null && summaryData.ghgAvoided !== 0);

    // Should never hit this, but including for testing purposes
    throw new Error(`Invalid page type "${page}" provided to pageHasData function!`);
}

export const noDataMessageText = (page, type = null, fleetHasAltFuels = null) => {
    let noDataMessage = '';
    let pdfReportNoDataMessage = ''
    if (page === 'home' || page === 'generated-emissions') {
        noDataMessage = 'No data to display. Try adjusting your selected date range or filters.'
        pdfReportNoDataMessage = 'No data to display. Try adjusting your selected date range or filters.'
    }
    //No data messaging for alternative fuel vehicle related tabs.
    if (page === 'avoided-emissions') {
        if (!fleetHasAltFuels) {
            noDataMessage = 'No alternative fuel vehicles in this fleet.'
            pdfReportNoDataMessage = 'There are no alternative fuel vehicles in this fleet. If you have questions, please contact your administrator.'
        } else {
            noDataMessage = 'No alternative fuel vehicle data to display. Try adjusting your selected date range or filters.'
            pdfReportNoDataMessage = 'There is no alternative fuel vehicle data based on your current filters. Try adjusting your date range or filters to determine your emission reductions.'
        }
    }

    if (type === PDF_TYPE) {
        return pdfReportNoDataMessage;
    } else {
        return noDataMessage;
    }
}

export function filterForSelectedClasses(selectedVehicleClasses, emissionData) {
    // filter out any vehicles in classes that aren't selected
    return emissionData.filter((v) => selectedVehicleClasses.indexOf(v.vehicle_class) > -1);
}

export function convertEmissionData(rawEmissionData, metricUnitPrefs, macroScale, userSettings) {
    let convertedData = []
    rawEmissionData.forEach(vehicle => {
        let convertedVehicle = {};
        Object.entries(vehicle).forEach(([key, value]) => {
            if (key.includes("fuelEcon")) {
                // Special case for fuel economy. Pick the correct version provided by the processor, and return only that value for fuel economy, to be displayed by the table.
                let fuelEconomyType = userSettings.liquid_fuel_economy;
                let fuelEconomyAccessor = null;
                if (fuelEconomyType === 'km/L') fuelEconomyAccessor = 'fuelEconKML';
                if (fuelEconomyType === 'km/gallon') fuelEconomyAccessor = 'fuelEconKMG';
                if (fuelEconomyType === 'MPG') fuelEconomyAccessor = 'fuelEconMPG';
                if (convertedVehicle['fuelEcon'] === undefined) { // No need to repeat this operation after the first fuel economy key has been hit
                    convertedVehicle['fuelEcon'] = vehicle[fuelEconomyAccessor];
                }
            } else if (key === "km" || key === "miles") {
                // Special case for distance traveled. Pick the correct version provided by the processor, and return only that value for distance, to be displayed by the table.
                if (convertedVehicle['distance'] === undefined) { // No need to repeat this operation after the first fuel economy key has been hit
                    convertedVehicle['distance'] = vehicle[metricUnitPrefs.distance ? 'km' : 'miles'];
                }
            } else {
                let convertedValue = convertData(key, value, metricUnitPrefs, macroScale, userSettings);
                convertedVehicle[key] = convertedValue;
            }
        });
        convertedData.push(convertedVehicle)
    });
    return convertedData;
}

export function convertSummaryData(rawSummaryData, metricUnitPrefs, macroScale, userSettings) {
    let convertedData = {};
    Object.entries(rawSummaryData).forEach(([key, value]) => {
        let convertedValue = convertData(key, value, metricUnitPrefs, macroScale, userSettings);
        convertedData[key] = convertedValue;
    });
    return convertedData;
}

export function convertGraphData(rawGraphData, metricUnitPrefs, macroScale, userSettings) {
    let convertedData = {};
    Object.entries(rawGraphData).forEach(([key, arr]) => {
        let convertedArr = arr.map((value) => convertData(key, value, metricUnitPrefs, macroScale, userSettings));
        convertedData[key] = convertedArr;
    });
    return convertedData;
}

// Format Luxon DateTime object to respect user settings
export function formatTimestamp(settings, value, excludeDay = false, forceShortYear = false){
    if(!value)return value;
    const date = formatDate(settings, value, excludeDay, forceShortYear);
    const time = formatTime(settings, value);

    return {"date": date, "time": time};
}

function formatDate(settings, value, excludeDay, forceShortYear) {
    // You can override the year display with forceShortYear
    // Similarly, you can hide display of the day with excludeDay
    
    const day = ('0' + value.day).slice(-2);
    const month = ('0' + (value.month)).slice(-2);
    const shortYear = value.year.toString().substring(2, 4);
    const fullYear = value.year;

    switch(settings.date_display){
        case 'dd/MM/yy':
            return `${excludeDay ? "" : (day+"/")}${month}/${shortYear}`;
        case 'dd/MM/yyyy':
            return `${excludeDay ? "" : (day+"/")}${month}/${forceShortYear ? shortYear : fullYear}`;
        case 'MM/dd/yy':
            return `${month}/${excludeDay ? "" : (day+"/")}${shortYear}`;
        case 'MM/dd/yyyy':
            return `${month}/${excludeDay ? "" : (day+"/")}${forceShortYear ? shortYear : fullYear}`;
        case 'yy/MM/dd':
            return `${shortYear}/${month}${excludeDay ? "" : ("/"+day)}`;
        case 'yyyy/MM/dd':
            return `${forceShortYear ? shortYear : fullYear}/${month}${excludeDay ? "" : ("/"+day)}`;
        default:
            return `${month}/${excludeDay ? "" : (day+"/")}${shortYear}`;
    }
}

function formatTime(settings, value) {
    switch(settings.ts_display){
        case "hh:mm:ss xm":
            return `${addLeadingZero(militaryToStandardHours(value.hour))}:${addLeadingZero(value.minute)}:${addLeadingZero(value.second)} ${amPm(value.hour)}`
        case "hh:mm:ss":
            return `${addLeadingZero(value.hour)}:${addLeadingZero(value.minute)}:${addLeadingZero(value.second)}`;
        case "hh:mm xm":
            return `${addLeadingZero(militaryToStandardHours(value.hour))}:${addLeadingZero(value.minute)} ${amPm(value.hour)}` 
        case "hh:mm":
            return `${addLeadingZero(value.hour)}:${addLeadingZero(value.minute)}`;
        default:
            return `${addLeadingZero(value.hour)}:${addLeadingZero(value.minute)}:${addLeadingZero(value.second)}`;
    }
}

function addLeadingZero(value) {
    if (value > 9) return value;
    else return `0${value}`;
}

function militaryToStandardHours(value) {
    if (value === 0)
        return 12;
    if (value > 12) {
        return value - 12;
    }
    return value;
}

function amPm(hour) {
    return hour > 11 && hour < 24 ? 'pm' : 'am'
}

export function determineFuelCost (vehicle, dbSettings){
    if(vehicle.backcasted_fuel_cost) {
        return vehicle.backcasted_fuel_cost;
    } else {
        if(vehicle.is_diesel) {
            return dbSettings.diesel_cost;
        } else {
            return dbSettings.gasoline_cost;
        }
    }
}

// Converts accented characters to unaccented characters and replaces special EU characters with their unaccented equivalents
// Used to enable accent-insensitive searching and comparison
export function convertToPlainText(value) {
    if (!value) return '';
    return value
        .normalize("NFD") // decomposes combined letters into letter + accent
        .toLowerCase()
        .replace(/\p{M}/gu, "") // removes all accent marks
        // replaces EU special letters that aren't marks
        .replace(/ß/g, "ss") 
        .replace(/æ/g, "ae")
        .replace(/œ/g, "oe")
        .replace(/ø/g, "o")
        .replace(/đ/g, "d")
        .replace(/ł/g, "l");
}